import { useFragment } from "@/generated";
import { LessonFieldsFragmentDoc, UnlinkLessonDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for unlinking a lesson and updating the Apollo cache.
 *
 * Converts a linked lesson copy to an independent lesson by:
 * 1. Setting isLinkedCopy to false
 * 2. Removing the lesson from the original lesson's linkedCopies
 *
 * **Cache Strategy:**
 * Uses cache.modify() to update both the unlinked lesson and the original lesson's
 * linkedCopies in the normalized cache.
 */
export function useUnlinkLesson() {
  const [unlinkLessonMutation, { loading }] = useMutation(UnlinkLessonDocument);

  const unlinkLesson = async (lessonId: string, originalLessonId: string | null) => {
    return withToast(
      async () => {
        const result = await unlinkLessonMutation({
          variables: {
            lessonId,
          },
          update(cache, { data }) {
            if (!data?.unlinkLesson) return;

            // Update the unlinked lesson
            cache.modify({
              id: `Lesson:${lessonId}`,
              fields: {
                isLinkedCopy: () => false,
              },
            });

            // Update the original lesson's linkedCopies
            if (originalLessonId) {
              const originalId = cache.identify({
                __typename: "Lesson",
                id: originalLessonId,
              });
              if (originalId) {
                cache.modify({
                  id: `Lesson:${originalLessonId}`,
                  fields: {
                    linkedCopies: (existingData, { readField }) => {
                      return existingData.filter(
                        (ref: Reference) => readField("id", ref) !== lessonId
                      );
                    },
                  },
                });
              }
            }
          },
        });

        const maskedLesson = result.data?.unlinkLesson;
        const unlinkedLesson = maskedLesson
          ? useFragment(LessonFieldsFragmentDoc, maskedLesson)
          : undefined;

        return unlinkedLesson;
      },
      {
        loading: "Unlinking lesson...",
        success: "Successfully unlinked lesson",
        error: "Failed to unlink lesson. Please try again.",
      },
      "unlinkLesson"
    );
  };

  return {
    unlinkLesson,
    loading,
  };
}
